import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, CheckCircle2, Zap, ArrowRight, Brain, MessageSquare } from 'lucide-react';

export const AISection: React.FC = () => {
    return (
        <section id="ai" className="ai-section relative py-32 overflow-hidden bg-[#0a0a0f]">
            {/* Ambient Background Glows - Monochrome */}
            <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-white/[0.02] blur-[120px] rounded-full -translate-y-1/2 pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-white/[0.02] blur-[120px] rounded-full translate-y-1/2 pointer-events-none" />

            <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
                {/* Left Column: Content */}
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    {/* Premium Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 shadow-[0_0_15px_rgba(99,102,241,0.1)] mb-8 group cursor-default hover:border-indigo-500/30 transition-colors">
                        <Sparkles className="w-4 h-4 text-indigo-400 animate-pulse" />
                        <span className="text-sm font-semibold text-indigo-100">
                            Powered by Gemini 2.0
                        </span>
                    </div>

                    <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight leading-[1.1]">
                        Your Personal <br />
                        <span className="text-zinc-500">
                            Context-Aware Research Assistant
                        </span>
                    </h2>

                    <p className="text-zinc-400 text-lg mb-10 leading-relaxed max-w-lg">
                        Stop drowning in tabs. Smart View analyzes your documents, images, and notes to
                        synthesize complex topics into quizzes, summaries, and action items.
                    </p>

                    <ul className="space-y-5 mb-10">
                        {[
                            { icon: Brain, text: "Synthesize complex topics instantly", color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20" },
                            { icon: Zap, text: "Auto-generate flashcards from notes", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
                            { icon: CheckCircle2, text: "Extract action items & to-do lists", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
                            { icon: MessageSquare, text: "Turn brainstorms into structured reports", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" }
                        ].map((item, i) => (
                            <motion.li
                                key={i}
                                className="flex items-center gap-4 text-zinc-300 group"
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 * i, duration: 0.5 }}
                            >
                                <div className={`w-8 h-8 rounded-lg ${item.bg} border ${item.border} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                    <item.icon className={`w-4 h-4 ${item.color}`} />
                                </div>
                                <span className="group-hover:text-white transition-colors">{item.text}</span>
                            </motion.li>
                        ))}
                    </ul>

                    <button className="flex items-center gap-2 text-white font-medium group hover:opacity-90 transition-opacity">
                        <span className="border-b border-transparent group-hover:border-white transition-colors">Explore Smart View</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                </motion.div>

                {/* Right Column: Premium Visual */}
                <motion.div
                    className="relative"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    {/* Main Glass Interface */}
                    <div className="relative bg-[#0F0F13]/80 backdrop-blur-2xl border border-white/10 rounded-2xl p-1 shadow-2xl ring-1 ring-white/5">
                        <div className="bg-[#0a0a0f] rounded-xl overflow-hidden border border-white/5 relative h-[500px] flex flex-col">

                            {/* Header */}
                            <div className="h-14 border-b border-white/5 flex items-center justify-between px-6 bg-white/[0.02]">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center shadow-lg border border-indigo-500/30">
                                        <Sparkles className="w-4 h-4 text-indigo-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-white">Smart View</div>
                                        <div className="text-[10px] items-center flex gap-1.5 text-zinc-500">
                                            <span className="relative flex h-1.5 w-1.5">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-20"></span>
                                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
                                            </span>
                                            Online • Analyzing 12 sources
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Chat Area */}
                            <div className="flex-1 p-6 space-y-6 overflow-hidden relative">
                                {/* Gradient Mesh background inside chat (Subtle) */}
                                <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent pointer-events-none" />

                                {/* User Message */}
                                <motion.div
                                    className="flex justify-end"
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                >
                                    <div className="bg-indigo-600/20 text-indigo-100 px-5 py-3 rounded-2xl rounded-tr-sm text-sm font-medium shadow-lg max-w-[85%] border border-indigo-500/20">
                                        Create study flashcards for this physics chapter.
                                    </div>
                                </motion.div>

                                {/* AI Response */}
                                <div className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex-shrink-0 flex items-center justify-center shadow-lg mt-1">
                                        <Sparkles className="w-4 h-4 text-indigo-400" />
                                    </div>
                                    <div className="flex-1 space-y-3">
                                        <motion.div
                                            className="bg-[#1c1c21] border border-white/10 rounded-2xl rounded-tl-sm p-5 shadow-xl"
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            whileInView={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: 0.6 }}
                                        >
                                            <p className="text-zinc-300 text-sm mb-4">I've analyzed your notes on Newtonian mechanics. Here are 5 key flashcards:</p>

                                            {/* Flashcards container */}
                                            <div className="space-y-2.5">
                                                {/* Card 1 */}
                                                <motion.div
                                                    className="group bg-[#2a2a35] hover:bg-[#32323e] border border-white/5 hover:border-white/10 p-4 rounded-xl cursor-pointer transition-all duration-300"
                                                    initial={{ opacity: 0, x: -10 }}
                                                    whileInView={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: 0.8 }}
                                                >
                                                    <div className="flex justify-between items-start mb-2">
                                                        <span className="text-[10px] font-mono text-zinc-400 uppercase tracking-wider">Concept</span>
                                                        <span className="text-[10px] text-zinc-600">Tap to flip</span>
                                                    </div>
                                                    <div className="text-zinc-200 font-medium text-sm">What is Newton's Second Law?</div>
                                                </motion.div>

                                                {/* Card 2 */}
                                                <motion.div
                                                    className="group bg-[#2a2a35] hover:bg-[#32323e] border border-white/5 hover:border-white/10 p-4 rounded-xl cursor-pointer transition-all duration-300"
                                                    initial={{ opacity: 0, x: -10 }}
                                                    whileInView={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: 0.9 }}
                                                >
                                                    <div className="flex justify-between items-start mb-2">
                                                        <span className="text-[10px] font-mono text-zinc-400 uppercase tracking-wider">Formula</span>
                                                        <span className="text-[10px] text-zinc-600">Tap to flip</span>
                                                    </div>
                                                    <div className="text-zinc-200 font-medium text-sm">F = ma (Force = mass × acceleration)</div>
                                                </motion.div>
                                            </div>
                                        </motion.div>

                                        {/* Action Chips */}
                                        <div className="flex gap-2">
                                            {['Summarize', 'Key Points', 'Quiz Me'].map((chip, k) => (
                                                <motion.button
                                                    key={chip}
                                                    className="text-xs bg-white/5 hover:bg-white/10 border border-white/5 px-3 py-1.5 rounded-full text-zinc-400 hover:text-white transition-colors"
                                                    initial={{ opacity: 0 }}
                                                    whileInView={{ opacity: 1 }}
                                                    transition={{ delay: 1.2 + (k * 0.1) }}
                                                >
                                                    {chip}
                                                </motion.button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Decorative Elements around visual (Monochrome) */}
                    <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/[0.03] blur-[60px] rounded-full pointer-events-none" />
                    <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/[0.03] blur-[60px] rounded-full pointer-events-none" />

                    {/* Floating Elements for Depth */}
                    <motion.div
                        className="absolute -right-8 top-1/4 bg-[#1e1e24] p-3 rounded-lg border border-indigo-500/30 shadow-xl"
                        animate={{ y: [0, -10, 0] }}
                        transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                    >
                        <Brain className="w-6 h-6 text-indigo-400" />
                    </motion.div>

                </motion.div>
            </div>
        </section>
    );
};
